//import liraries
import { useEffect, useState } from 'react';
import { Avatar, Box, Divider, IconButton, TextField, Typography, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { request } from '../../Utils/request';
import SendIcon from '@mui/icons-material/Send';
import { 
  setComments, 
  addComment, 
  setLoading, 
  setError,
  type Comment,
  type CommentState 
} from '../../store/slices/CommentSlice';
import { RootState } from '../../store/rootReducers';

// TypeScript interfaces for comment data
interface User {
    _id: string;
    firstName: string;
    lastName: string;
    username: string;
    profilePhoto: {
        url?: string;
    };
}

interface CommentsResponse {
    success: boolean;
    data: {
        comments: Comment[];
        pagination: {
            page: number;
            limit: number;
            total: number;
            pages: number;
        };
    };
}

// create a component
const Comments = ({ postId }: { postId: string }) => {
    const [comment, setComment] = useState('');
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { comments, loading, error } = useSelector((state: RootState) => state.comment);

    const handleCommentSubmit = async () => {
        if (!comment.trim()) return;
        
        try {
            dispatch(setLoading(true));
            const response = await request(`/posts/${postId}/comment`, {
                method: 'POST',
                body: {
                    content: comment
                },
                authorization: true
            }) as { success: boolean; data?: Comment; message?: string };

            if (response.success && response.data) {
                // Add the new comment to the Redux store
                dispatch(addComment(response.data));
                setComment(''); // Clear the input
            } else {
                dispatch(setError(response.message || 'Failed to add comment'));
            }
        } catch (error) {
            dispatch(setError('Failed to add comment'));
        } finally {
            dispatch(setLoading(false));
        }
    };

    const fetchComments = async () => {
        try {
            dispatch(setLoading(true));
            const response = await request(`/posts/${postId}/comments`, {
                method: 'GET',
                authorization: true
            }) as CommentsResponse;

            if (response.success && response.data) {
                const comments = response.data.comments || [];
                dispatch(setComments(comments));
            } else {
                dispatch(setError('Failed to fetch comments'));
            }
        } catch (error) {
            dispatch(setError('Failed to fetch comments'));
        } finally {
            dispatch(setLoading(false));
        }
    };

    useEffect(() => {
        fetchComments();
    }, [postId]);

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleCommentSubmit();
        }
    };

    if (loading && comments.length === 0) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', padding: 2 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <div>
            {error && (
                <Typography color="error" sx={{ padding: 1, textAlign: 'center' }}>
                    {error}
                </Typography>
            )}
            
            {comments.map((comment) => (
                <Box key={comment._id} sx={{ display: 'flex', flexDirection: 'row', gap: 1, borderRadius: 1, padding: 1 }}>
                    <Box 
                        sx={{ display: 'flex', flexDirection: 'row', gap: 1, justifyContent: 'center', alignItems: 'center', cursor: 'pointer' }} 
                        onClick={()=>{ navigate(`/profile/${comment.user._id}`); }}
                    >
                        <Avatar src={comment.user.profilePhoto?.url} />
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                        <Typography fontWeight={600}>{comment.user.username}</Typography>
                        <Typography fontWeight={400}>{comment.content}</Typography>
                    
                    </Box>
                </Box>
            ))}
            
            <Divider />
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, justifyContent: 'center', alignItems: 'center', padding: 1 }}>
                <TextField
                    fullWidth
                    sx={{
                        backgroundColor: 'white',
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                                border: 'none',
                            },
                            '&:hover fieldset': {
                                border: 'none',
                            },
                            '&.Mui-focused fieldset': {
                                border: 'none',
                            },
                        },
                    }}
                    label="Comment"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    onKeyPress={handleKeyPress}
                    disabled={loading}
                    multiline
                    maxRows={3}
                />
                <IconButton 
                    color="primary" 
                    onClick={handleCommentSubmit}
                    disabled={loading || !comment.trim()}
                >
                    {loading ? <CircularProgress size={20} /> : <SendIcon sx={{ color: 'black' }} />}
                </IconButton>
            </Box>
        </div>
    );
};

//make this component available to the app
export default Comments;
